import React, { useState } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { hideLoading, showLoading } from '../../lib/uiService';
import hdBonusService from '../../services/hd.bonus.service';
import { toast } from 'react-toastify';

type AnswersData = {
  healthParticipation: boolean;
  developmentParticipation: boolean;
  bookTitle: string;
  bookClubParticipation: string;
  keyLearnings: string;
  companyInsights: string;
  interestTopics: string;
  biggestTakeaway: string;
  surprisingAnalogy: string;
  recommendation: string;
};

interface HDBonusFormProps {
  onSubmissionSuccess: () => void;
  bonusData?: any;
  answersData?: AnswersData;
  isDisabled?: boolean;
}

const HDBonusForm: React.FC<HDBonusFormProps> = ({ onSubmissionSuccess, isDisabled = false, answersData }) => {
  const [submitting, setSubmitting] = useState(false);

  // Helper function for conditional field validation based on multiple dependencies
  const conditionalTextValidation = (fieldName: string) => {
    return Yup.string().test({
      name: `check-${fieldName}`,
      test: function (value) {
        // Access the parent values
        const { developmentParticipation, bookClubParticipation } = this.parent;

        // Check if this field should be required
        const shouldBeRequired =
          developmentParticipation && (bookClubParticipation === '2' || bookClubParticipation === '3');

        if (shouldBeRequired) {
          if (!value) {
            return this.createError({
              path: fieldName,
              message: 'Required',
            });
          }

          if (value.length > 500) {
            return this.createError({
              path: fieldName,
              message: 'Must be 500 characters or less',
            });
          }
        }

        return true;
      },
    });
  };

  // Form validation schema
  const validationSchema = Yup.object({
    healthParticipation: Yup.boolean().required(),
    developmentParticipation: Yup.boolean().required(),
    bookTitle: Yup.string().when('developmentParticipation', {
      is: true,
      then: () => Yup.string().max(100, 'Must be 100 characters or less').required('Required'),
      otherwise: () => Yup.string(),
    }),
    bookClubParticipation: Yup.string().when('developmentParticipation', {
      is: true,
      then: () => Yup.string().required('Required'),
      otherwise: () => Yup.string(),
    }),
    keyLearnings: conditionalTextValidation('keyLearnings'),
    companyInsights: conditionalTextValidation('companyInsights'),
    interestTopics: conditionalTextValidation('interestTopics'),
    biggestTakeaway: conditionalTextValidation('biggestTakeaway'),
    surprisingAnalogy: conditionalTextValidation('surprisingAnalogy'),
    recommendation: conditionalTextValidation('recommendation'),
  });

  const formik = useFormik({
    initialValues: {
      healthParticipation: false,
      developmentParticipation: false,
      bookTitle: '',
      bookClubParticipation: '',
      keyLearnings: '',
      companyInsights: '',
      interestTopics: '',
      biggestTakeaway: '',
      surprisingAnalogy: '',
      recommendation: '',
      ...answersData,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        showLoading();
        // Calculate bonus amount
        let bonusAmount = 0;
        if (values.healthParticipation) bonusAmount += 50;
        if (values.developmentParticipation) bonusAmount += 50;

        // API call to submit bonus using our service
        await hdBonusService.SubmitHDBonus({
          ...values,
          bonusAmount,
          bonusPeriod: moment().format('YYYY-MM'),
        });

        // Reset form and show success
        formik.resetForm();
        onSubmissionSuccess();

        toast.success('Health and Development Bonus submitted successfully!');
        hideLoading();
      } catch (error) {
        hideLoading();
        toast.error('Error submitting bonus. Please try again.');
        console.error('Error submitting bonus:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Determine if additional questions should be enabled
  const shouldEnableAdditionalQuestions =
    formik.values.developmentParticipation && [2, 3].includes(Number(formik.values.bookClubParticipation));

  // Calculate remaining characters for text fields
  const getRemainingChars = (field: string, maxLength: number) => {
    return maxLength - (formik.values[field]?.length || 0);
  };

  return (
    <Form onSubmit={formik.handleSubmit}>
      {/* Required Questions */}
      <Form.Group className="mb-4">
        <Form.Label>Did you participate in the BBP Health Bonus?</Form.Label>
        <div>
          <Form.Check
            type="switch"
            id="healthParticipation"
            label={formik.values.healthParticipation ? 'Yes' : 'No'}
            checked={formik.values.healthParticipation}
            disabled={isDisabled}
            onChange={() => formik.setFieldValue('healthParticipation', !formik.values.healthParticipation)}
            inline
          />
        </div>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Did you participate in the BBP Development Bonus?</Form.Label>
        <div>
          <Form.Check
            type="switch"
            id="developmentParticipation"
            label={formik.values.developmentParticipation ? 'Yes' : 'No'}
            checked={formik.values.developmentParticipation}
            disabled={isDisabled}
            onChange={() => {
              const newValue = !formik.values.developmentParticipation;
              formik.setFieldValue('developmentParticipation', newValue);
              if (!newValue) {
                // Reset related fields if toggling off
                formik.setFieldValue('bookTitle', '');
                formik.setFieldValue('bookClubParticipation', '');
                formik.setFieldValue('keyLearnings', '');
                formik.setFieldValue('companyInsights', '');
                formik.setFieldValue('interestTopics', '');
                formik.setFieldValue('biggestTakeaway', '');
                formik.setFieldValue('surprisingAnalogy', '');
                formik.setFieldValue('recommendation', '');
              }
            }}
            inline
          />
        </div>
      </Form.Group>

      {/* Book Club and Development Questions */}
      {formik.values.developmentParticipation && (
        <>
          <Form.Group className="mb-4">
            <Form.Label>What book did you read?</Form.Label>
            <Form.Control
              type="text"
              id="bookTitle"
              name="bookTitle"
              value={formik.values.bookTitle}
              disabled={isDisabled}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.bookTitle && !!formik.errors.bookTitle}
              maxLength={100}
            />
            <Form.Text className="text-muted">{getRemainingChars('bookTitle', 100)} characters remaining</Form.Text>
            <Form.Control.Feedback type="invalid">{formik.errors.bookTitle}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Was the book part of the company book club discussion?</Form.Label>
            <Form.Select
              id="bookClubParticipation"
              name="bookClubParticipation"
              value={formik.values.bookClubParticipation}
              disabled={isDisabled}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.bookClubParticipation && !!formik.errors.bookClubParticipation}
            >
              <option value="">Select an option</option>
              <option value="1">Yes - and I will be participating in the required group discussion</option>
              <option value="2">Yes - and I will be on PTO or other approved absence during the allotted time</option>
              <option value="3">No - this book will not be discussed during a company book club</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{formik.errors.bookClubParticipation}</Form.Control.Feedback>
          </Form.Group>

          {/* Additional questions that appear only if needed */}
          {shouldEnableAdditionalQuestions && (
            <>
              <Form.Group className="mb-4">
                <Form.Label>What are 3 key learnings from this book?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="keyLearnings"
                  name="keyLearnings"
                  value={formik.values.keyLearnings}
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.keyLearnings && !!formik.errors.keyLearnings}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {getRemainingChars('keyLearnings', 500)} characters remaining
                </Form.Text>
                <Form.Control.Feedback type="invalid">{formik.errors.keyLearnings}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  Do you feel the book highlights any strengths or opportunities of BBP as a company or of our team as
                  individuals?
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="companyInsights"
                  name="companyInsights"
                  value={formik.values.companyInsights}
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.companyInsights && !!formik.errors.companyInsights}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {getRemainingChars('companyInsights', 500)} characters remaining
                </Form.Text>
                <Form.Control.Feedback type="invalid">{formik.errors.companyInsights}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  Did this book pique your interest in any topics that you would like to learn more about?
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="interestTopics"
                  name="interestTopics"
                  value={formik.values.interestTopics}
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.interestTopics && !!formik.errors.interestTopics}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {getRemainingChars('interestTopics', 500)} characters remaining
                </Form.Text>
                <Form.Control.Feedback type="invalid">{formik.errors.interestTopics}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  What&apos;s the biggest takeaway you would apply to the way you approach running your business?
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="biggestTakeaway"
                  name="biggestTakeaway"
                  value={formik.values.biggestTakeaway}
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.biggestTakeaway && !!formik.errors.biggestTakeaway}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {getRemainingChars('biggestTakeaway', 500)} characters remaining
                </Form.Text>
                <Form.Control.Feedback type="invalid">{formik.errors.biggestTakeaway}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>What analogy surprised you the most and why?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="surprisingAnalogy"
                  name="surprisingAnalogy"
                  value={formik.values.surprisingAnalogy}
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.surprisingAnalogy && !!formik.errors.surprisingAnalogy}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {getRemainingChars('surprisingAnalogy', 500)} characters remaining
                </Form.Text>
                <Form.Control.Feedback type="invalid">{formik.errors.surprisingAnalogy}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Would you recommend this book to other members of our team? Why or why not?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="recommendation"
                  name="recommendation"
                  value={formik.values.recommendation}
                  disabled={isDisabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.recommendation && !!formik.errors.recommendation}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {getRemainingChars('recommendation', 500)} characters remaining
                </Form.Text>
                <Form.Control.Feedback type="invalid">{formik.errors.recommendation}</Form.Control.Feedback>
              </Form.Group>
            </>
          )}
        </>
      )}

      {isDisabled ? null : (
        <div className="d-grid gap-2">
          <Button variant="primary" type="submit" disabled={submitting || !formik.isValid}>
            {submitting ? 'Submitting...' : 'Submit Bonus'}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default HDBonusForm;
